import { storage } from "@/lib/firebase";
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import api from "@/lib/api";

// If Firebase Storage rules refuse a browser upload into app_content/ (e.g. the rules in
// firebase/storage.rules aren't published yet), upload the same file through the Command
// Centre server instead, which writes with the service account. Same folder, same kind of URL.
const canUseServer = (path) => String(path).startsWith("app_content/");
const refused = (e) => e?.code === "storage/unauthorized" || e?.code === "storage/unauthenticated";
async function serverUpload(path, file, onProgress) {
  const form = new FormData();
  form.append("file", file);
  form.append("path", path);
  const { data } = await api.post("/cc/storage-upload", form, {
    onUploadProgress: (ev) => onProgress && ev.total && onProgress(Math.round((ev.loaded / ev.total) * 100)),
  });
  return data.url;
}

// Upload an image File to Firebase Storage (bucket brugo-core-97965.firebasestorage.app)
// and return its public download URL. Used for Customer-App hero/banner assets.
export async function uploadImage(path, file) {
  try {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  } catch (e) {
    if (refused(e) && canUseServer(path)) return serverUpload(path, file);
    throw e;
  }
}

// Resumable upload for larger files (photos AND videos). Uploads directly from
// the browser as the signed-in user — avoids routing big payloads through the
// backend/ingress (which was returning Cloudflare 520 on the dead admin proxy).
export async function uploadFileResumable(path, file, onProgress) {
  try {
    return await browserUploadResumable(path, file, onProgress);
  } catch (e) {
    if (refused(e) && canUseServer(path)) { onProgress && onProgress(0); return serverUpload(path, file, onProgress); }
    throw e;
  }
}

function browserUploadResumable(path, file, onProgress) {
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(ref(storage, path), file, {
      contentType: file.type || "application/octet-stream",
    });
    task.on(
      "state_changed",
      (snap) => onProgress && onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      async () => resolve(await getDownloadURL(task.snapshot.ref))
    );
  });
}
